import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiStar, HiBookOpen, HiNewspaper } from 'react-icons/hi';
import { parseMetadata } from '../utils/metadataParser';

const HomePage = () => {
  const navigate = useNavigate();
  const [dykContent, setDykContent] = useState('');
  const [updatesContent, setUpdatesContent] = useState('');
  const [featuredArticle, setFeaturedArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDykContent();
    fetchUpdatesContent();
    fetchFeaturedArticle();
  }, []);

  const parseMarkdownList = (text, headerToRemove) => {
    // Remove the markdown header and extract content
    const content = text.replace(new RegExp(`^# ${headerToRemove}\\s*\\n+`), '');
    // Convert markdown bullet points to HTML list
    const htmlContent = content
      .split('\n')
      .filter(line => line.trim())
      .map(line => {
        if (line.trim().startsWith('* ')) {
          let lineContent = line.trim().substring(2);
          // Convert markdown links [text](url) to HTML links
          lineContent = lineContent.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="/$2">$1</a>');
          return `<li>${lineContent}</li>`;
        }
        return line;
      })
      .join('');
    
    return `<ul>${htmlContent}</ul>`;
  };

  const fetchDykContent = async () => {
    try {
      const response = await fetch('/content/dyk.md');
      if (response.ok) {
        const text = await response.text();
        setDykContent(parseMarkdownList(text, 'Did You Know\\?'));
      }
    } catch (error) {
      console.error('Error loading Did You Know content:', error);
      setDykContent('Interesting facts about Montreal\'s music history coming soon...');
    }
  };

  const fetchUpdatesContent = async () => {
    try {
      const response = await fetch('/content/updates.md');
      if (response.ok) {
        const text = await response.text();
        // Parse updates markdown (different format than DYK)
        const content = text.replace(/^# Updates\s*\n+/, '');
        // Convert markdown headings and paragraphs
        const htmlContent = content
          .split('\n\n')
          .filter(section => section.trim())
          .map(section => {
            const lines = section.split('\n');
            if (lines[0].startsWith('## ')) {
              const heading = lines[0].replace('## ', '');
              const content = lines.slice(1).join(' ');
              return `<div class="update-item"><h4>${heading}</h4><p>${content}</p></div>`;
            }
            return `<p>${section}</p>`;
          })
          .join('');
        
        setUpdatesContent(htmlContent);
      }
    } catch (error) {
      console.error('Error loading Updates content:', error);
      setUpdatesContent('Latest updates coming soon...');
    }
  };

  const fetchFeaturedArticle = async () => {
    try {
      const response = await fetch('/content/featured.md');
      if (response.ok) {
        const text = await response.text();
        const { metadata, contentWithoutMetadata } = parseMetadata(text);
        
        setFeaturedArticle({
          title: metadata.title,
          category: metadata.category,
          date: metadata.date,
          image: metadata.image,
          link: metadata.link,
          content: contentWithoutMetadata.replace(/^# Content\s*\n+/, '')
        });
      }
    } catch (error) {
      console.error('Error loading featured article:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReadMore = () => {
    if (featuredArticle?.link) {
      navigate(featuredArticle.link);
    }
  };

  return (
    <div className="home-page-container">
      <div className="home-layout">
        <div className="featured-article-section">
          <h2 className="section-title">
            <HiStar className="section-icon" />
            Featured Article
          </h2>
          <div className="featured-article-content">
            {loading || !featuredArticle ? (
              <p>Loading featured article...</p>
            ) : (
              <>
                <div className="featured-article-header">
                  <h3 className="featured-article-title">{featuredArticle.title}</h3>
                  <span className="featured-article-category">
                    {featuredArticle.category} • {featuredArticle.date}
                  </span>
                </div>
                
                {featuredArticle.image && (
                  <div className="featured-article-image">
                    <img src={featuredArticle.image} alt={featuredArticle.title} />
                  </div>
                )}
                
                <div className="featured-article-text">
                  <div dangerouslySetInnerHTML={{ 
                    __html: featuredArticle.content.replace(/\n\n/g, '</p><p>').replace(/^/, '<p>').replace(/$/, '</p>')
                  }} />
                </div>
                
                <button className="read-more-button" onClick={handleReadMore}>
                  Read Full Article
                </button>
              </>
            )}
          </div>
        </div>
        
        <div className="sidebar-sections">
          <div className="did-you-know-section">
            <h3 className="section-title">
              <HiBookOpen className="section-icon" />
              Did You Know?
            </h3>
            <div className="did-you-know-content">
              {loading ? (
                <p>Loading...</p>
              ) : (
                <div dangerouslySetInnerHTML={{ __html: dykContent }} />
              )}
            </div>
          </div>
          
          <div className="updates-section">
            <h3 className="section-title">
              <HiNewspaper className="section-icon" />
              Updates
            </h3>
            <div className="updates-content">
              {loading ? (
                <p>Loading...</p>
              ) : (
                <div dangerouslySetInnerHTML={{ __html: updatesContent }} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;